import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { styled } from "styled-components";
import { addBulkCompany } from "ui-io/queries/add-bulk-companies"
import {
  Box,
  CustomSelect,
  ItemType,
  NoData,
  Pagination,
  TitleComponent,
  CustomCard,
} from "ui-components";
import toasts from "../../../utils/toasts";
import FiltersContainer from "../../../components/filters-container";
import { parseCSVToJSON, transformCompanyData } from "../../../utils";
import CompanyTable from "./company-table";
import { Colors } from "@blueprintjs/core";
import { TableCompanyDataT, TransformedCompanyT } from "../../../utils/constants";

const PageSizeOptions: ItemType[] = [
  { label: "25", value: "25" },
  { label: "50", value: "50" },
  { label: "100", value: "100" },
  { label: "200", value: "200" },
];

const ConfigPage = () => {
  const [currentCountryCode, setCountryCode] = useState("");
  //this originalTableData is for storing actual data before filtering
  const [originalTableData, setOriginalTableData] = useState<{
    headers: string[];
    rows: Record<string, string>[];
  }>({
    headers: [],
    rows: [],
  });
  const [currentStatusCode, setCurrentStatusCode] = useState<string[]>([]);

  const [selectedFile, setSelectedFile] = useState<undefined | File>();
  //this tabledata is for filterpurpose
  const [tableData, setTableData] = useState<{
    headers: string[];
    rows: Record<string, string>[];
  }>({
    headers: [],
    rows: [],
  });
  const [selectedTableData, setSelectedTableData] = useState<
    Record<string, string>[]
  >([]);
  //Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState<ItemType>(PageSizeOptions[0]);
  const [itemsPerPage, setItemsPerPage] = useState(Number(pageSize.value));
  const [totalNumberOfPages, setTotalNumberOfPages] = useState(0);
  const [isPagination, setIsPagination] = useState(false);

  //onchange of tabledata I have to update Pagination
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.files?.length) {
      const file = event.target.files[0];
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (result && typeof result === "string") {
          const parsedData = parseCSVToJSON(result);
          setTableData(parsedData);
          setOriginalTableData(parsedData);
        }
      };
      reader.readAsText(file);
    }
  };
  
  function toggle(source: HTMLInputElement) {
    let checkboxes = document.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][name="select"]'
    );
    for (let i = 0; i < checkboxes.length; i++) {
      if (checkboxes[i] != source) checkboxes[i].checked = source.checked;
    }
    if (source.checked) {
      setSelectedTableData(tableData.rows);
    } else {
      setSelectedTableData([]);
    }
  }
  //oncheck of the checkbox selected rows will be sent
  const handleRowSelect = (row: Record<string, string>, checked: boolean) => {
    setSelectedTableData((prev) =>
      checked ? [...prev, row] : prev.filter((r) => r !== row)
    );
  };
  //status
  // pagination purpose
  useEffect(() => {
    if (
      tableData &&
      tableData.rows &&
      tableData.rows.length > 0 &&
      itemsPerPage <= tableData.rows.length
    ) {
      const totalPages = Math.ceil(tableData.rows.length / itemsPerPage);
      setTotalNumberOfPages(totalPages);
      setIsPagination(totalPages > 1);
    } else {
      setTotalNumberOfPages(0);
      setIsPagination(false);
    }
  }, [tableData.rows, itemsPerPage, originalTableData]);
  // Reset to first page when table data changes
  useEffect(() => {
    setCurrentPage(0);
  }, [tableData.rows]);
  // Add this function before your return statement
  const getCurrentPageData = () => {
    if (!tableData || !tableData.rows || tableData.rows.length === 0) {
      return { headers: tableData.headers, rows: [] };
    }

    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRows = tableData.rows.slice(startIndex, endIndex);

    return {
      headers: tableData.headers,
      rows: paginatedRows,
    };
  };

  //filterting
  useMemo(() => {
    if (currentStatusCode.length == 0) {
      setTableData(originalTableData);
    } else {
      const filteredData = originalTableData.rows.filter((row) => {
        return currentStatusCode.includes(row["company_status"].toLowerCase());
      });
      setTableData({ headers: originalTableData.headers, rows: filteredData });
    }
  }, [currentStatusCode, originalTableData, selectedFile]);
  //remove row
  const handleRemove = () => {
    if (selectedTableData.length > 0) {
      setOriginalTableData({
        headers: originalTableData.headers,
        rows: originalTableData.rows.filter(
          (row) => !selectedTableData.includes(row)
        ),
      });
      toasts.success(`${selectedTableData.length}  Company removed!`);
      setSelectedTableData([]);
    } else {
      toasts.error(
        "No rows selected. Please select one or more rows before attempting to remove."
      );
      return;
    }
  };
  //reset
  const handleOnResetClick = () => {
    setTableData({ headers: [], rows: [] });
  	setOriginalTableData({ headers: [], rows: [] });
    setCurrentStatusCode([]);
		setSelectedTableData([])
    setCountryCode("");
		const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
		if (fileInput) {
			fileInput.value = "";
			fileInput.name = ""
		}
		setSelectedFile(undefined);
  };
	const updateCompanyMutation = useMutation(addBulkCompany, {
			onSuccess: () => {
				toasts.success("successfully uploaded");
			},
			onError: () => {
				toasts.error("Could not upload Companies.");
			},
		});
		
	const handleUpdate=(data:TableCompanyDataT[])=>{
		const formattedData:TransformedCompanyT[] = transformCompanyData(data);
		updateCompanyMutation.mutate(formattedData);
	}
  return (
    <>
      <Box flexDir="row" gap={"medium"} justify="between">
        <Box flexDir="col">
          <TitleComponent marginTop="0px">Upload Company</TitleComponent>
        </Box>
        <Box flexDir="col" width={"3/4"}>
          <Box flexDir="row" gap={"medium"} justify="between">
            {originalTableData?.rows?.length > +PageSizeOptions[0].value && (
							<Box flexDir="row" gap={"medium"} ml={"small"} items="center" justify="center">
              <CustomSelect
								flexDir="row"
                title={"Page Size"}
                isActiveItem={true}
                label={pageSize.label || "Select"}
                items={PageSizeOptions}
                values={[pageSize.value]}
                onSelect={(item) => {
                  setPageSize(item);
                  setItemsPerPage(Number(item.value));
                  setCurrentPage(0);
                }}
              />
								{(tableData.rows.length > 0 || originalTableData?.rows.length > 0) &&
									<Box flex="row" justify="center" items="center" gap={"small"}>
										<Label>Total</Label>
										<TitleComponent fontSize="15px" marginTop="10px">
											{tableData.rows.length}
										</TitleComponent>
									</Box>
								}

							</Box>
            )}
            {totalNumberOfPages >= 1 && (
              <>
                <Box mb="small" justify="end">
                  <Pagination
                    cursor={currentPage}
                    count={totalNumberOfPages}
                    onPaginate={(val) => setCurrentPage(val)}
                    maxCells={7}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Box flexDir="row" gap={"medium"} justify="between">
        <Box flexDir="col" width={"1/4"}>
          <CustomCard marginTop={"5px"}>
            <FiltersContainer
              selectedFile={selectedFile}
              handleFileChange={handleFileChange}
              setCountryCode={setCountryCode}
              currentCountryCode={currentCountryCode}
              setCurrentStatusCode={setCurrentStatusCode}
              currentStatusCode={currentStatusCode}
              handleRemove={handleRemove}
              handleUpdate={handleUpdate}
              handleOnResetClick={handleOnResetClick}
							selectedTableData={selectedTableData}

            />
          </CustomCard>
        </Box>
        <Box flexDir="col" width={"3/4"}>
          <CustomCardContainer
            marginTop={"5px"}
            isTableData={tableData?.rows?.length > 0}
          >
            {originalTableData?.rows?.length > 0 ? (
							<>

								<CompanyTable
									tableData={getCurrentPageData()}
									toggle={toggle}
									handleRowSelect={handleRowSelect}
									selectedTableData={selectedTableData}
								/>
							</>
            ) : (
              <NoData
                description={"Please Upload Company Excel File"}
                title={"No File Selected"}
                icon={"warning-sign"}
              />
            )}
          </CustomCardContainer>
        </Box>
      </Box>
      {isPagination && totalNumberOfPages > 1 && (
        <Box mt="medium" justify="end">
          <Pagination
            cursor={currentPage}
            count={totalNumberOfPages}
            onPaginate={(val) => setCurrentPage(val)}
            maxCells={7}
          />
        </Box>
      )}
    </>
  );
};

export default ConfigPage;

const CustomCardContainer = styled(CustomCard)<{ isTableData?: boolean }>`
  width: 100%;
  flex: 1;
  align-self: flex-start;
  overflow: ${({ isTableData }) => (isTableData ? "scroll" : "hidden")};
`;
const Label = styled.span`
		font-size: 15px;
		color: ${({ theme: { darkMode } }) => darkMode ? Colors.GRAY5 : Colors.GRAY1};
`;